import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import { DATE_FORMAT } from '../../api/api.types';
import { DayPicker } from 'react-day-picker';
import { detect } from 'detect-browser';

interface DatePickerState {
  open: boolean;
  focused: boolean;
  value: string | undefined;
  isInvalid?: boolean;
  date?: Dayjs;
  id: string;
}

interface DatePickerProps {
  date?: Dayjs;
  onChange: (dates?: Dayjs) => void;
  inputStyle?: React.CSSProperties;
  isInvalid?: boolean;
  className?: string;
  dateOrderIncorrect?: boolean;
  isEdit?: boolean;
  isFilter?: boolean;
  id?: string;
}

export enum PromoCodesDatePickerEvent {
  PICKUP = 'pickupDateFilter',
  REQUEST = 'requestDateFilter'
}

export enum KeyEventEnum {
  ENTER = 'Enter'
}

export enum IntervalDatePickerEvent {
  FROM = 'From',
  TO = 'To'
}

const DatePicker: React.FC<DatePickerProps> = (props) => {


  const [ pickerState, setPickerState ] = useState<DatePickerState>({
    value: props.date ? props.date.format(DATE_FORMAT) : undefined,
    date: props.date,
    open: false,
    focused: !!props.date,
    isInvalid: props.isInvalid,
    id: props.id ? props.id : '0'
  });
  const [ month, setMonth ] = useState(props.date ? props.date.toDate() : new Date());
  const [ hasClickedMonthChange, setHasClickedMonthChange ] = useState(false);
  const monthChangeClickedRef = useRef(hasClickedMonthChange);

  useEffect(() => {
    monthChangeClickedRef.current = hasClickedMonthChange;
  }, [ hasClickedMonthChange ]);

  useEffect(() => {
    setPickerState({
      open: false,
      value: props.date ? props.date.format(DATE_FORMAT) : undefined,
      date: props.date,
      focused: !!props.date,
      isInvalid: props.isInvalid,
      id: props.id ? props.id : '0'
    });
    setMonth(props.date ? props.date.toDate() : new Date());
  }, [ props.date, props.isInvalid, props.id ]);

  const toggle = (open: boolean) => {
    setPickerState({ ...pickerState, open: open });
  };

  const onBlur = (c: string | undefined) => {
    const browser = detect();
    if (browser?.name === 'safari') {
      setTimeout(() => {
        if (monthChangeClickedRef.current) {
          setHasClickedMonthChange(false);
          return;
        }
        handleBlurActions(c);
      }, 100);
    } else {
      handleBlurActions(c);
    }
    setHasClickedMonthChange(false);
  };

  const handleBlurActions = (c: string | undefined) => {
    if (!checkClassNameNotDatepicker(c)) {
      return;
    }
    if (!pickerState.value) {
      setPickerState({ ...pickerState, open: false, isInvalid: !props.isFilter });
      props.onChange(undefined);
      return;
    }
    if (pickerState.value && dayjs(pickerState.value, DATE_FORMAT, true).isValid()) {
      setPickerState({ ...pickerState, isInvalid: false, date: dayjs(pickerState.value, DATE_FORMAT) });
      props.onChange(dayjs(pickerState.value, DATE_FORMAT));
    } else if (!props.isFilter) setPickerState({ ...pickerState, isInvalid: true });
  };

  const onValueChange = (value: ChangeEvent<HTMLInputElement>) => {
    const dateString = value.target.value;
    const oldPickerState = pickerState;
    if (dateString.length && dayjs(dateString, DATE_FORMAT, true).isValid()) {
      const newDate = dayjs(dateString, DATE_FORMAT);
      oldPickerState.date = newDate;
      setMonth(newDate.toDate());
    } else {
      oldPickerState.date = undefined;
    }
    oldPickerState.value = dateString;
    oldPickerState.open = true;
    setPickerState({ ...oldPickerState });
  };

  const handleDatePickerContainerOnBlur = (targetClassName?: string) => {
    setHasClickedMonthChange(false);
    if (!targetClassName) {
      toggle(false);
      return;
    }
    if (checkClassNameNotDatepicker(targetClassName))
      toggle(false);
  };

  const checkClassNameNotDatepicker = (cl: string | undefined): boolean => {
    if (!cl) return true;
    for (const l of [ 'date-input-box-' + pickerState.id, 'rdp', 'date-selection-' + pickerState.id ]) {
      if (cl.includes(l))
        return false;
    }
    return true;
  };

  const handleDateSelected = (d: Date | undefined) => {
    setHasClickedMonthChange(false);
    if (d) {
      setPickerState({
        ...pickerState, date: dayjs(d), open: false, value: dayjs(d).format(DATE_FORMAT),
        isInvalid: false
      });
      setMonth(d);
      props.onChange(dayjs(d));
    }
  };

  const handleEnterPressed = (e: any) => {
    setHasClickedMonthChange(false);
    if (e.key === KeyEventEnum.ENTER) {
      if (!pickerState.value) {
        setPickerState({ ...pickerState, open: false, isInvalid: !props.isFilter });
        props.onChange(undefined);
        return;
      }
      if (pickerState.value && dayjs(pickerState.value, DATE_FORMAT, true).isValid()) {
        setPickerState({
          ...pickerState, isInvalid: false, date: dayjs(pickerState.value, DATE_FORMAT),
          open: false
        });
        props.onChange(dayjs(pickerState.value, DATE_FORMAT));
      } else if (!props.isFilter) setPickerState({ ...pickerState, isInvalid: true, open: false });
    }
  };

  return (
    <div className={'datepicker-container'}>
      <input
        value={pickerState.value ?? ''}
        id={props.id}
        onClick={(e: any) => {
          toggle(true);
          e.target.focus();
        }}
        placeholder={DATE_FORMAT}
        className={`date-picker-input date-input-box-${pickerState.id} form-control${(pickerState.isInvalid || props.dateOrderIncorrect)
        && ' is-invalid' || ''} ${props.className ? props.className : ''}`}
        onFocus={() => toggle(true)}
        onBlur={(e) => onBlur(e.relatedTarget?.className)}
        onChange={(value) => onValueChange(value)}
        style={props.inputStyle || {}}
        onKeyPress={handleEnterPressed}
        autoComplete="off"
      />
      {pickerState.open &&
          <div
              onBlur={(e) => handleDatePickerContainerOnBlur(e.relatedTarget?.className)}
              className={'date-selection-container date-selection-' + pickerState.id}
              tabIndex={0}
          >
              <DayPicker mode="single" selected={pickerState.date?.toDate()}
                         onSelect={(d: Date | undefined) => handleDateSelected(d)}
                         disabled={!props.isEdit ? { before: new Date() } : undefined}
                         initialFocus={!!props.date}
                         month={month}
                         onMonthChange={(m) => {
                           setHasClickedMonthChange(true);
                           setMonth(m);
                         }}
              />
          </div>}
    </div>
  );
};

export default DatePicker;
